import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastAction } from "../../../components/ui/toast/toast";
import { useToast } from "../../../components/ui/toast/use-toast";
import useRepoStore from "../../../services/RepoService";
import RepositoriesPageHeader from "../components/RepositoriesPageHeader";
import RepositoriesToolbar from "../components/RepositoriesToolbar";
import RepositoriesList from "../components/RepositoriesList";
import RepositoryLoadingState from "../components/RepositoryLoadingState";
import RepositoryEmptyState from "../components/RepositoryEmptyState";
import RepositoryErrorState from "../components/RepositoryErrorState";
import {
  RepositoryFilterMode,
  RepositorySortMode,
  RepositoryViewModel,
  toRepositoryViewModel,
} from "../types";
import {
  extractOwners,
  filterRepositories,
  sortRepositories,
} from "../utils/repositoryListUtils";

const RepositoriesPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { repo, getRepositories, toggleFavorite } = useRepoStore();

  const [isLoading, setIsLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<RepositoryFilterMode>("all");
  const [owner, setOwner] = useState("");
  const [sort, setSort] = useState<RepositorySortMode>("favorites");

  const repositories = useMemo<RepositoryViewModel[]>(
    () => (repo || []).map(toRepositoryViewModel),
    [repo]
  );

  const owners = useMemo(() => extractOwners(repositories), [repositories]);

  const visibleRepositories = useMemo(() => {
    const filtered = filterRepositories(repositories, { query, filter, owner });
    return sortRepositories(filtered, sort);
  }, [repositories, query, filter, owner, sort]);

  const favoritesCount = useMemo(
    () => repositories.filter((item) => item.isFavorite).length,
    [repositories]
  );

  const loadRepositories = useCallback(async () => {
    setIsLoading(true);
    setErrorText("");
    try {
      const data = await getRepositories();
      if (!data) {
        setErrorText("There was a problem loading repositories.");
        toast({
          variant: "destructive",
          title: "Repositories request failed",
          description: "Could not load repositories from GitHub permissions scope.",
          action: (
            <ToastAction altText="Retry" onClick={loadRepositories}>
              Retry
            </ToastAction>
          ),
        });
      }
    } catch (error) {
      setErrorText("Unexpected error while loading repositories.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [getRepositories, toast]);

  useEffect(() => {
    if (!repo) {
      loadRepositories();
    }
  }, [repo, loadRepositories]);

  const handleToggleFavorite = useCallback(
    async (item: RepositoryViewModel) => {
      const success = await toggleFavorite(item.owner, item.name, item.isFavorite);
      if (!success) {
        toast({
          variant: "destructive",
          title: "Favorite update failed",
          description: "Could not update repository favorite status.",
        });
      }
    },
    [toggleFavorite, toast]
  );

  const handleOpenRepository = useCallback(
    (item: RepositoryViewModel) => {
      navigate(`/edit/${item.owner}/${item.name}`);
    },
    [navigate]
  );

  const isFiltered = Boolean(query.trim()) || filter === "favorites" || Boolean(owner);

  return (
    <div className="space-y-4">
      <RepositoriesPageHeader
        totalCount={repositories.length}
        visibleCount={visibleRepositories.length}
        favoritesCount={favoritesCount}
      />

      <RepositoriesToolbar
        query={query}
        filter={filter}
        owner={owner}
        owners={owners}
        sort={sort}
        isLoading={isLoading}
        onQueryChange={setQuery}
        onFilterChange={setFilter}
        onOwnerChange={setOwner}
        onSortChange={setSort}
        onRefresh={loadRepositories}
      />

      {isLoading ? <RepositoryLoadingState /> : null}

      {!isLoading && errorText ? (
        <RepositoryErrorState message={errorText} onRetry={loadRepositories} />
      ) : null}

      {!isLoading && !errorText && visibleRepositories.length === 0 ? (
        <RepositoryEmptyState isFiltered={isFiltered} />
      ) : null}

      {!isLoading && !errorText && visibleRepositories.length > 0 ? (
        <RepositoriesList
          repositories={visibleRepositories}
          onToggleFavorite={handleToggleFavorite}
          onOpenRepository={handleOpenRepository}
        />
      ) : null}
    </div>
  );
};

export default RepositoriesPage;
